import {
  BreakdownHoverCard,
  BreakdownHoverCardHeader,
} from "~/components/reusable/hovercard/BreakdownHoverCard"
import { HoverCardFooter } from "~/components/reusable/hovercard/HoverCard"

const missingInputsFormatter = new Intl.ListFormat("en", { type: "conjunction" })

type Props = {
  title: string
  trigger: React.ReactNode
  /** Context for the figures, such as the period they cover. */
  description?: string
  /**
   * One section per direct child, separated by a rule. Use `HoverCardRowWrapper` for a row group,
   * `MetricBreakdownNullState` when the metric can't be computed, and `MetricBreakdownFormula` last.
   */
  children: React.ReactNode
}

/**
 * Hover card explaining how a computed figure was derived: the inputs it used and the arithmetic
 * that produced it.
 *
 * Legacy surface — the HoverCard family is not in design-system/APPROVED.md (see MER-622).
 */
export function MetricBreakdownHoverCard({ title, trigger, description, children }: Props) {
  return (
    <BreakdownHoverCard trigger={trigger} className="w-90">
      <BreakdownHoverCardHeader title={title} description={description} />
      <div className="[&>*+*]:border-t [&>*+*]:border-base">{children}</div>
    </BreakdownHoverCard>
  )
}

/** Stands in for the rows when an input is missing, naming what the metric needs. */
export function MetricBreakdownNullState({
  label,
  missing,
  recorded,
}: {
  /** Subject of "{label} can't be calculated." */
  label: string
  /** Names of the absent inputs, read out as "a, b, and c". */
  missing: string[]
  /** A figure that was found, when only the absent stock history is holding the result back. */
  recorded?: { label: string; value: string }
}) {
  return (
    <div className="px-4 py-3 paragraph-sm text-fg-muted space-y-1">
      <p>
        {label} can&apos;t be calculated. Missing{" "}
        <span className="font-medium text-fg-base">{missingInputsFormatter.format(missing)}</span>{" "}
        in this period.
      </p>
      {recorded && (
        <p>
          {recorded.label} of <span className="font-medium text-fg-base">{recorded.value}</span>{" "}
          recorded, but no inventory months with stock were found.
        </p>
      )}
    </div>
  )
}

/** Closing section spelling out the calculation with unabbreviated figures. */
export function MetricBreakdownFormula({ children }: { children: React.ReactNode }) {
  return (
    <HoverCardFooter className="bg-subtle py-2.5">
      <p className="paragraph-sm text-fg-muted font-mono">{children}</p>
    </HoverCardFooter>
  )
}
